import { useState } from 'react';
import { errorMessage } from '../api/client';
import { eventsApi, newEventId } from '../api/endpoints';
import type { TimelineEvent } from '../api/types';
import { eventTypeLabel } from '../domain/events';
import { formatDateTime, fromDateTimeInput, nowForInput } from '../lib/format';
import { Field } from './Field';
import { Modal } from './Modal';
import { Notice } from './Notice';

/**
 * Annuler ne supprime rien : un événement d'annulation est ajouté au journal
 * et référence celui qui perd son effet.
 */
export function CancelEventModal({
  target,
  onClose,
  onSaved,
}: {
  target: TimelineEvent;
  onClose: () => void;
  onSaved: (event: TimelineEvent) => void;
}) {
  const [occurredAt, setOccurredAt] = useState(nowForInput);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    eventsApi
      .append({
        id: newEventId(),
        workstationId: target.workstationId,
        type: 'annulation',
        occurredAt: fromDateTimeInput(occurredAt),
        reason,
        payload: { evenementId: target.id },
      })
      .then(({ event: saved }) => onSaved(saved))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Annuler un événement"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Renoncer
          </button>
          <button
            type="submit"
            form="formulaire-annulation"
            className="bouton bouton--danger"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : "Écrire l'annulation"}
          </button>
        </>
      }
    >
      <form id="formulaire-annulation" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}

        <Notice tone="info">
          L'événement <strong>{eventTypeLabel(target.type)}</strong> du{' '}
          {formatDateTime(target.occurredAt)} restera dans le journal, marqué comme
          annulé. Rien n'est supprimé.
        </Notice>

        <div className="grille-champs">
          <Field label="Date de l'annulation">
            <input
              type="datetime-local"
              value={occurredAt}
              required
              onChange={(event) => setOccurredAt(event.target.value)}
            />
          </Field>

          <Field label="Motif de l'annulation" wide>
            <textarea
              value={reason}
              required
              maxLength={2000}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
